// ── Hold-to-play access gate. ──
// Calls the api `GET {VITE_API_URL}/access?wallet=<base58>` to check whether a wallet
// holds enough $TOKEN to enter the world. If the api is unreachable (or there is no
// wallet), we fall back to a "demo (no wallet)" mode so the game is always playable in dev.

export type AccessMode = 'wallet' | 'demo';

export interface AccessResult {
  /** Whether the player may enter the world. Demo mode is always allowed. */
  allowed: boolean;
  mode: AccessMode;
  wallet: string | null;
  /** Held $TOKEN balance (UI units) if the api reported it. */
  balance?: number;
  /** Minimum hold required to play, if the api reported it. */
  required?: number;
  /** Human-readable reason, used for UI messaging. */
  reason: string;
}

const API_URL: string | undefined = import.meta.env.VITE_API_URL;

function demo(reason: string): AccessResult {
  return { allowed: true, mode: 'demo', wallet: null, reason };
}

/**
 * Check play access for a wallet. Resilient: any failure resolves to demo mode rather
 * than rejecting, so a missing/offline api never blocks single-player.
 *
 * Expected api response shape (best-effort parsed):
 *   { allowed: boolean, balance?: number, required?: number, reason?: string }
 */
export async function checkAccess(wallet: string | null): Promise<AccessResult> {
  if (!wallet) {
    return demo('No wallet connected — playing in demo mode.');
  }
  if (!API_URL) {
    return demo('VITE_API_URL not set — playing in demo mode.');
  }

  try {
    const url = `${API_URL.replace(/\/$/, '')}/access?wallet=${encodeURIComponent(wallet)}`;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(timeout);

    if (!res.ok) {
      return demo(`Access service returned ${res.status} — playing in demo mode.`);
    }

    // Server (`/access`) shape: { wallet, uiAmount, hasAccess, minHold }. Map it to AccessResult.
    const data = (await res.json()) as {
      uiAmount?: number;
      hasAccess?: boolean;
      minHold?: number;
    };

    const allowed = data.hasAccess === true;
    const balance = typeof data.uiAmount === 'number' ? data.uiAmount : undefined;
    const required = typeof data.minHold === 'number' ? data.minHold : undefined;
    return {
      allowed,
      mode: 'wallet',
      wallet,
      balance,
      required,
      reason: allowed
        ? 'Hold-to-play requirement met.'
        : `Hold ${required != null ? required.toLocaleString() : 'enough'} $MONSTARA to unlock the full game.`,
    };
  } catch {
    // network error / timeout / CORS — never hard-block the player in dev
    return demo('Access service unreachable — playing in demo mode.');
  }
}
